import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import {
  ArrowLeft,
  Plus,
  Search,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  User,
  Users,
  FileText,
  X,
  Check,
  Upload,
  Download,
  LogOut,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  ShoppingBag,
  FileEdit,
  Printer
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import NotificationBell from "../components/NotificationBell";
import ImportClientsModal from "../components/ImportClientsModal";
import LetterEditorModal from "../components/LetterEditorModal";

const Clients = ({ user }) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [formData, setFormData] = useState({
    n_tessera: "",
    nome: "",
    cognome: "",
    codice_fiscale: "",
    data_nascita: "",
    indirizzo: "",
    cap: "",
    comune: "",
    provincia: "",
    telefono: "",
    email: "",
    quota: "",
    note: ""
  });
  const [saving, setSaving] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [letterClient, setLetterClient] = useState(null);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    fetchClients();
  }, [currentPage, itemsPerPage]);

  const fetchClients = async (searchQuery = search) => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };
      if (searchQuery) {
        params.search = searchQuery;
      }
      const response = await axios.get(`${API}/clients`, {
        params,
        withCredentials: true
      });
      setClients(response.data.clients);
      setTotalClients(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error("Errore caricamento clienti:", error);
      if (error.response?.status === 401) {
        toast.error("Sessione scaduta");
        navigate("/login");
      } else {
        toast.error("Errore nel caricamento dei clienti");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on search
    fetchClients(search);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/login");
    } catch (error) {
      navigate("/login");
    }
  };

  const openNewForm = () => {
    setEditingClient(null);
    setFormData({
      n_tessera: "",
      nome: "",
      cognome: "",
      codice_fiscale: "",
      data_nascita: "",
      indirizzo: "",
      cap: "",
      comune: "",
      provincia: "",
      telefono: "",
      email: "",
      quota: "",
      note: ""
    });
    setShowForm(true);
  };

  const openEditForm = (client) => {
    setEditingClient(client);
    setFormData({
      n_tessera: client.n_tessera || "",
      nome: client.nome || "",
      cognome: client.cognome || "",
      codice_fiscale: client.codice_fiscale || "",
      data_nascita: client.data_nascita || "",
      indirizzo: client.indirizzo || "",
      cap: client.cap || "",
      comune: client.comune || "",
      provincia: client.provincia || "",
      telefono: client.telefono || "",
      email: client.email || "",
      quota: client.quota || "",
      note: client.note || ""
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.nome || !formData.cognome || !formData.codice_fiscale) {
      toast.error("Nome, cognome e codice fiscale sono obbligatori");
      return;
    }

    setSaving(true);
    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.client_id}`, formData, {
          withCredentials: true
        });
        toast.success("Cliente aggiornato");
      } else {
        await axios.post(`${API}/clients`, formData, {
          withCredentials: true
        });
        toast.success("Cliente creato");
      }
      setShowForm(false);
      fetchClients(search);
    } catch (error) {
      console.error("Errore salvataggio:", error);
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (clientId) => {
    try {
      await axios.delete(`${API}/clients/${clientId}`, {
        withCredentials: true
      });
      toast.success("Cliente eliminato");
      setDeleteConfirm(null);
      fetchClients(search);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const exportToCSV = async () => {
    try {
      // Fetch all clients for export (high limit)
      const response = await axios.get(`${API}/clients`, {
        params: { page: 1, limit: 10000, search },
        withCredentials: true
      });
      const allClients = response.data.clients;
      
      const headers = ["N.Tessera", "Nome", "Cognome", "Codice Fiscale", "Data Nascita", "Indirizzo", "CAP", "Comune", "Provincia", "Telefono", "Email"];
      const csvContent = [
        headers.join(";"),
        ...allClients.map(c => [
          c.n_tessera || "",
          c.nome || "",
          c.cognome || "",
          c.codice_fiscale || "",
          c.data_nascita || "",
          c.indirizzo || "",
          c.cap || "",
          c.comune || "",
          c.provincia || "",
          c.telefono || "",
          c.email || ""
        ].join(";"))
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "clienti.csv";
      link.click();
      toast.success(`Esportati ${allClients.length} clienti`);
    } catch (error) {
      toast.error("Errore nell'esportazione");
    }
  };

  const getRoleBadge = () => {
    switch (user?.role) {
      case "admin":
        return <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Admin</span>;
      case "titolare":
        return <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Titolare</span>;
      default:
        return <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">Collaboratore</span>;
    }
  };

  // Open WhatsApp chat
  const openWhatsApp = (phone) => {
    if (!phone) return;
    // Clean phone number (remove spaces, dashes)
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, "");
    // Add Italy prefix if not present
    const fullPhone = cleanPhone.startsWith("+") ? cleanPhone : `+39${cleanPhone}`;
    window.open(`https://wa.me/${fullPhone.replace("+", "")}`, "_blank");
  };

  // Open letter editor modal
  const openLetterEditor = (client) => {
    setLetterClient(client);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Modern Header */}
      <header className="sticky top-0 z-50">
        <div className="h-[2px] bg-gradient-to-r from-transparent via-blue-500 to-transparent"></div>
        <div className="backdrop-blur-xl bg-white/70 border-b border-white/20 shadow-lg shadow-slate-900/5">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/dashboard")}
                  className="gap-2 rounded-xl hover:bg-slate-100/80"
                  data-testid="back-button"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Dashboard
                </Button>
                <div className="h-6 w-px bg-slate-300" />
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center shadow-lg">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-slate-900">
                      Scheda Clienti
                    </h1>
                    <p className="text-xs text-slate-500">{totalClients} clienti registrati</p>
                  </div>
                </div>
              </div>
            
              <div className="flex items-center gap-3">
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-2 py-1.5 rounded-xl bg-white/80 hover:bg-slate-100/80 transition-colors border border-slate-200/50 shadow-sm" data-testid="user-menu">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 p-[2px]">
                      <div className="w-full h-full rounded-[10px] bg-white flex items-center justify-center">
                        <span className="text-sm font-bold bg-gradient-to-br from-blue-600 to-purple-600 bg-clip-text text-transparent">
                          {user?.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-slate-700 hidden sm:inline">{user?.name?.split(' ')[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 p-2 rounded-xl">
                  <div className="px-3 py-2 mb-2 rounded-lg bg-slate-50">
                    <p className="font-semibold text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.role === "admin" ? "Amministratore" : user?.role}</p>
                  </div>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 rounded-lg py-2.5" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-3" />
                    <span className="font-medium">Esci dall'account</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Search and Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2 w-full sm:w-auto">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca per nome, cognome, CF, telefono..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" variant="secondary" data-testid="search-btn">
                Cerca
              </Button>
            </form>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={exportToCSV} className="gap-2" data-testid="export-btn">
                <Download className="w-4 h-4" />
                Esporta CSV
              </Button>
              {isAdmin && (
                <Button 
                  variant="outline" 
                  onClick={() => setShowImportModal(true)} 
                  className="gap-2 text-green-600 border-green-300 hover:bg-green-50"
                  data-testid="import-btn"
                >
                  <Upload className="w-4 h-4" />
                  Importa Excel
                </Button>
              )}
              <Button onClick={openNewForm} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="new-client-btn">
                <Plus className="w-4 h-4" />
                Nuovo Cliente
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-4 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <p className="text-blue-100 text-sm">Totale Clienti</p>
                <p className="text-3xl font-bold">{totalClients}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Clients List */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        ) : clients.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">Nessun cliente trovato</h3>
            <p className="text-slate-500 mb-4">
              {search ? "Prova a modificare la ricerca" : "Inizia aggiungendo il primo cliente"}
            </p>
            <Button onClick={openNewForm} className="gap-2">
              <Plus className="w-4 h-4" />
              Aggiungi Cliente
            </Button>
          </div>
        ) : (
          <div className="grid gap-3">
            {clients.map((client) => (
              <div
                key={client.client_id}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:shadow-md transition-all group"
                data-testid={`client-card-${client.client_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                        {client.nome?.charAt(0)?.toUpperCase()}{client.cognome?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">
                          {client.nome} {client.cognome}
                        </h3>
                        <div className="flex items-center gap-2 flex-wrap">
                          {client.n_tessera && (
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                              Tessera: {client.n_tessera}
                            </span>
                          )}
                          {/* Order count badge */}
                          <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${
                            client.order_count > 0 
                              ? "bg-green-100 text-green-700" 
                              : "bg-slate-100 text-slate-500"
                          }`}>
                            <ShoppingBag className="w-3 h-3" />
                            {client.order_count || 0} ordini
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <span className="font-mono">{client.codice_fiscale}</span>
                      </div>
                      
                      {client.telefono && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone className="w-4 h-4 text-slate-400" />
                          <a href={`tel:${client.telefono}`} className="hover:text-blue-600">
                            {client.telefono}
                          </a>
                          {/* WhatsApp button */}
                          <button
                            onClick={() => openWhatsApp(client.telefono)}
                            className="p-1 rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
                            title="Apri WhatsApp"
                            data-testid={`whatsapp-${client.client_id}`}
                          >
                            <MessageCircle className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      
                      {client.email && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <a href={`mailto:${client.email}`} className="hover:text-blue-600 truncate">
                            {client.email}
                          </a>
                        </div>
                      )}
                      
                      {client.comune && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span>{client.comune} ({client.provincia})</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex flex-col gap-2 items-end">
                    {/* Letter button - always visible */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openLetterEditor(client)}
                      className="gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50"
                      title="Modifica e genera lettera commerciale"
                      data-testid={`letter-${client.client_id}`}
                    >
                      <FileEdit className="w-4 h-4" />
                      Lettera
                    </Button>
                    
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditForm(client)}
                          className="text-slate-600 hover:text-blue-600"
                          data-testid={`edit-client-${client.client_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(client)}
                          className="text-slate-600 hover:text-red-600"
                          data-testid={`delete-client-${client.client_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && totalClients > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mt-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Items per page selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Mostra</span>
                <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                  <SelectTrigger className="w-20" data-testid="items-per-page-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="30">30</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-slate-600">clienti per pagina</span>
              </div>

              {/* Page info */}
              <div className="text-sm text-slate-600">
                Pagina <span className="font-semibold">{currentPage}</span> di{" "}
                <span className="font-semibold">{totalPages}</span>
                {" "}({totalClients} clienti totali)
              </div>

              {/* Navigation arrows */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="gap-1"
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Indietro
                </Button>
                
                {/* Page numbers */}
                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === currentPage ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-8 h-8 p-0 ${pageNum === currentPage ? "bg-blue-600" : ""}`}
                        data-testid={`page-${pageNum}-btn`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="gap-1"
                  data-testid="next-page-btn"
                >
                  Avanti
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingClient ? "Modifica Cliente" : "Nuovo Cliente"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="n_tessera">N. Tessera</Label>
              <Input
                id="n_tessera"
                value={formData.n_tessera}
                onChange={(e) => setFormData({ ...formData, n_tessera: e.target.value })}
                placeholder="000123"
                data-testid="input-n_tessera"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="codice_fiscale">Codice Fiscale *</Label>
              <Input
                id="codice_fiscale"
                value={formData.codice_fiscale}
                onChange={(e) => setFormData({ ...formData, codice_fiscale: e.target.value.toUpperCase() })}
                placeholder="RSSMRA80A01H501A"
                className="font-mono uppercase"
                data-testid="input-codice_fiscale"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Mario"
                data-testid="input-nome"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cognome">Cognome *</Label>
              <Input
                id="cognome"
                value={formData.cognome}
                onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                placeholder="Rossi"
                data-testid="input-cognome"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="data_nascita">Data di Nascita</Label>
              <Input
                id="data_nascita"
                type="date"
                value={formData.data_nascita}
                onChange={(e) => setFormData({ ...formData, data_nascita: e.target.value })}
                data-testid="input-data_nascita"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="telefono">Telefono</Label>
              <Input
                id="telefono"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                placeholder="333 1234567"
                data-testid="input-telefono"
              />
            </div>
            
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="mario.rossi@email.com"
                data-testid="input-email"
              />
            </div>
            
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="indirizzo">Indirizzo</Label>
              <Input
                id="indirizzo"
                value={formData.indirizzo}
                onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                placeholder="Via Roma, 1"
                data-testid="input-indirizzo"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cap">CAP</Label>
              <Input
                id="cap"
                value={formData.cap}
                onChange={(e) => setFormData({ ...formData, cap: e.target.value })}
                placeholder="00100"
                data-testid="input-cap"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="comune">Comune</Label>
              <Input
                id="comune"
                value={formData.comune}
                onChange={(e) => setFormData({ ...formData, comune: e.target.value })}
                placeholder="Roma"
                data-testid="input-comune"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="provincia">Provincia</Label>
              <Input
                id="provincia"
                value={formData.provincia}
                onChange={(e) => setFormData({ ...formData, provincia: e.target.value })}
                placeholder="RM"
                data-testid="input-provincia"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="quota">Quota</Label>
              <Input
                id="quota"
                value={formData.quota}
                onChange={(e) => setFormData({ ...formData, quota: e.target.value })}
                placeholder="20"
                data-testid="input-quota"
              />
            </div>
            
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="note">Note</Label>
              <Input
                id="note"
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder="Note aggiuntive..."
                data-testid="input-note"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} data-testid="cancel-btn">
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-client-btn">
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              {editingClient ? "Salva Modifiche" : "Crea Cliente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <ImportClientsModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => fetchClients(search)}
      />

      {/* Letter Editor Modal */}
      <LetterEditorModal
        open={!!letterClient}
        onClose={() => setLetterClient(null)}
        client={letterClient}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare {deleteConfirm?.nome} {deleteConfirm?.cognome}. Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteConfirm?.client_id)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Clients;
